import { RefObject, useMemo } from 'react';
import useDimensionsObserver from '@/utils/use-dimensions-observer';
import useTrackResize from '@/utils/use-track-resize';
import useTrackScrollDimensions from '@/utils/use-track-scroll-dimensions';
import useTrackBoundingClient from '@/utils/use-track-bounding-client';

export type UseTriggerDimensionTrackingParams = {
  triggerRef: RefObject<HTMLButtonElement | null>;
  open: boolean;
  recalcutePosition?: number;
  trackScrollElRef?: RefObject<HTMLElement | null>;
  trackOnWindowScroll: boolean;
  triggerBoundsRecalc: number;
};

export type UseTriggerDimensionTrackingReturn = {
  triggerBounds: DOMRect | null;
  triggerDimensions: DOMRectReadOnly | null;
};

/**
 * Consolidates all dimension and bounds tracking logic for the trigger element.
 * Combines multiple tracking hooks into one cohesive unit.
 */
export function useTriggerDimensionTracking({
  triggerRef,
  open,
  recalcutePosition,
  trackScrollElRef,
  trackOnWindowScroll,
  triggerBoundsRecalc,
}: UseTriggerDimensionTrackingParams): UseTriggerDimensionTrackingReturn {
  const triggerDimensions = useDimensionsObserver({
    ref: triggerRef,
  });

  const windowResize = useTrackResize({
    trackWindow: true,
    trackType: 'debounced',
    debounceTime: 100,
  });

  const { top: scrollElTop } = useTrackScrollDimensions({
    containerRef: trackScrollElRef,
    pause: !open,
  });

  const summedTriggerDimensionsForRecalc = useMemo(
    () =>
      (triggerDimensions?.bottom || 0) +
      (triggerDimensions?.top || 0) +
      (triggerDimensions?.right || 0) +
      (triggerDimensions?.left || 0) +
      triggerBoundsRecalc,
    [triggerDimensions, triggerBoundsRecalc]
  );

  const triggerBounds = useTrackBoundingClient({
    ref: triggerRef,
    debounceTime: 0,
    rerunCounter:
      summedTriggerDimensionsForRecalc +
      (recalcutePosition || 0) +
      windowResize.width +
      windowResize.height +
      scrollElTop,
    open,
    trackOnWindowResize: !!open,
    trackOnWindowScroll: trackOnWindowScroll && open,
  });

  return {
    triggerBounds,
    triggerDimensions,
  };
}
